import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { WIDGET_KIND_DRAG_TYPE } from '../dragTypes';
import { WidgetHost } from '../../WidgetHost';
import type { IPointBindingSource } from '../../binding';
import type { WidgetDefinition, WidgetKind } from '../../widgets';
import { createPalettePreviewSource } from './previewSource';
import {
  THUMB_H,
  THUMB_W,
  THUMB_W_WIDE,
  TILE_GAP,
  isWide,
  thumbGeometry,
} from './thumbGeometry';
import {
  WIDGET_CATALOG_LIST,
  WIDGET_GROUPS,
  type WidgetCatalogEntry,
} from './widgetCatalog';
import './WidgetPalette.css';

/**
 * Der MIME-Typ, unter dem die Palette die Widget-Art in den Ziehvorgang legt.
 *
 * Eigener Typ und nicht `text/plain`: die Rasterflaeche nimmt genau diesen
 * entgegen und weist alles andere ab. Ein aus dem Browser hereingezogener
 * Text oder eine Datei sollen nicht versehentlich ein Widget erzeugen. Muster
 * dafuer steht in `pages/PlantSchemaPage.tsx`, dort heisst der Typ
 * `application/x-hvacloud-schema`.
 */
export { WIDGET_KIND_DRAG_TYPE as WIDGET_KIND_MIME } from '../dragTypes';

/*
 * Die Masse und die Rechnung der Miniatur stehen in `thumbGeometry.ts`, samt
 * Begruendung fuer `transform: scale()`. Sie sind von dort auch geprueft;
 * hier wird nur angewendet.
 */

export interface WidgetPaletteProps {
  /**
   * Klick-Weg. Die Palette legt selbst nichts am Projekt an, sie kennt den
   * Store nicht: sie sagt nur, was der Autor genommen hat, und die Seite
   * entscheidet, wo es hinkommt.
   *
   * Beide Wege werden gebraucht. Ziehen ist der natuerliche, wenn die
   * Zielstelle sichtbar ist. Klicken ist der einzige, der auf einem
   * Bedienpanel mit Finger funktioniert und der einzige, der noch geht, wenn
   * die Zielstelle gerade ausserhalb des Sichtbereichs liegt.
   */
  onPick(kind: WidgetKind): void;
  /** Optionaler Klassenhaken fuer das Layout der Seite. */
  class?: string;
}

export function WidgetPalette(props: WidgetPaletteProps) {
  const [query, setQuery] = useState('');
  /**
   * Die zugeklappten Abschnitte, nicht die aufgeklappten.
   *
   * Herum gedacht, und das ist Absicht: neu ist alles offen. Wer eine
   * Gruppe nie anfasst, soll sie trotzdem gesehen haben; der Bestand ist
   * das, was der Auftraggeber vermisst hat.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  /**
   * Eine Quelle fuer alle Miniaturen, angelegt beim ersten Rendern.
   *
   * Sie haelt nur Werte und Ringpuffer, keine Verbindung: das Abraeumen
   * besteht darin, dass die Widgets ihre Abos beim Abhaengen schliessen,
   * und das tun sie selbst.
   */
  const source = useMemo(() => createPalettePreviewSource(), []);

  /**
   * Die Beispiel-Widgets, einmal erzeugt.
   *
   * `preview()` liefert bei jedem Aufruf ein frisches Objekt. Bei jedem
   * Rendern neu aufgerufen bekaeme jedes Widget eine neue Definition in die
   * Hand, und die Renderer, die auf die Identitaet ihrer Definition achten
   * (Anlagenschema, Trend), baeuten ihre Abos jedes Mal neu auf.
   */
  const previews = useMemo(() => {
    const out = new Map<WidgetKind, WidgetDefinition | null>();
    for (const entry of WIDGET_CATALOG_LIST) out.set(entry.kind, entry.preview());
    return out;
  }, []);

  const needle = query.trim().toLowerCase();

  const groups = useMemo(() => {
    const matches = (entry: WidgetCatalogEntry) => needle === ''
      || entry.title.toLowerCase().includes(needle)
      || entry.description.toLowerCase().includes(needle)
      // Auch ueber die Art suchbar: wer aus dem gespeicherten JSON kommt,
      // kennt `stage-cascade` und nicht "Kaskade".
      || entry.kind.includes(needle);

    return WIDGET_GROUPS
      .map((group) => ({
        group,
        entries: WIDGET_CATALOG_LIST.filter((e) => e.group === group.key && matches(e)),
      }))
      .filter((g) => g.entries.length > 0);
  }, [needle]);

  const empty = groups.length === 0;

  function toggleGroup(key: string): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  return (
    <nav
      class={'hv-wp' + (props.class ? ` ${props.class}` : '')}
      aria-label="Widget-Palette"
      style={{
        '--hv-wp-thumb-w': `${THUMB_W}px`,
        '--hv-wp-thumb-h': `${THUMB_H}px`,
        '--hv-wp-thumb-w-wide': `${THUMB_W_WIDE}px`,
        '--hv-wp-tile-gap': `${TILE_GAP}px`,
      }}
    >
      <div class="hv-wp__search">
        <input
          class="hv-wp__search-input"
          type="search"
          value={query}
          placeholder="Suchen"
          aria-label="Widgets durchsuchen"
          onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
        />
      </div>

      <div class="hv-wp__scroll">
        {empty && (
          <p class="hv-wp__empty">Kein Widget passt zu „{query}".</p>
        )}
        {groups.map(({ group, entries }) => {
          // Waehrend einer Suche zaehlt das Zuklappen nicht. Ein Treffer,
          // der hinter einem zugeklappten Abschnitt liegt, ist kein Treffer,
          // sondern ein Raetsel: die Suche zeigte einen Abschnitt mit
          // Zaehler an und darunter nichts.
          const open = needle !== '' || !collapsed.has(group.key);
          return (
            <section class="hv-wp__group" key={group.key}>
              <h4 class="hv-wp__group-head">
                <button
                  type="button"
                  class="hv-wp__group-toggle"
                  aria-expanded={open}
                  title={group.hint}
                  onClick={() => toggleGroup(group.key)}
                >
                  <span class={`hv-wp__chevron${open ? ' hv-wp__chevron--open' : ''}`} aria-hidden="true" />
                  <span class="hv-wp__group-label">{group.label}</span>
                  <span class="hv-wp__group-count">{entries.length}</span>
                </button>
              </h4>
              {open && (
                <ul class="hv-wp__grid">
                  {entries.map((entry) => (
                    <li
                      key={entry.kind}
                      class={'hv-wp__cell' + (isWide(entry) ? ' hv-wp__cell--wide' : '')}
                    >
                      <PaletteItem
                        entry={entry}
                        preview={previews.get(entry.kind) ?? null}
                        source={source}
                        onPick={props.onPick}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Eine Kachel: Miniatur, Name, Rastermass.
 *
 * KEIN `<button>` mehr, obwohl es einer war. Der Grund steckt im Inhalt: in
 * der Miniatur stehen echte Widgets, und die bringen eigene Knoepfe,
 * Schieberegler und Eingabefelder mit. Ein Knopf im Knopf ist ungueltiges
 * HTML, und der Browser haengt die verschachtelten Elemente beim Auswerten
 * aus dem Knopf heraus. Die Kachel ist deshalb ein `div` mit `role="button"`
 * und eigener Tastaturbehandlung, was dieselbe Bedienbarkeit ergibt, ohne
 * den Aufbau zu brechen.
 */
function PaletteItem(props: {
  entry: WidgetCatalogEntry;
  preview: WidgetDefinition | null;
  source: IPointBindingSource;
  onPick(kind: WidgetKind): void;
}) {
  const { entry } = props;

  function pick(): void {
    props.onPick(entry.kind);
  }

  return (
    <div
      class="hv-wp__item"
      role="button"
      tabIndex={0}
      draggable
      title={`${entry.title}: ${entry.description}`}
      onClick={pick}
      onKeyDown={(e) => {
        // Enter und Leertaste sind das, was ein Knopf von sich aus koennte.
        // Beim Leerzeichen muss das Blaettern unterdrueckt werden, sonst
        // springt die Palette beim Auswaehlen nach unten.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pick();
        }
      }}
      onDragStart={(e) => {
        if (!e.dataTransfer) return;
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData(WIDGET_KIND_DRAG_TYPE, entry.kind);
        // Zusaetzlich als Klartext, damit ein Ziehvorgang, der versehentlich
        // in einem Textfeld endet, dort wenigstens etwas Lesbares ablegt und
        // nicht nichts.
        e.dataTransfer.setData('text/plain', entry.kind);
      }}
    >
      <Thumbnail entry={entry} preview={props.preview} source={props.source} />
      <span class="hv-wp__item-title">{entry.title}</span>
      <span class="hv-wp__item-size" aria-hidden="true">
        {entry.colSpan}&times;{entry.rowSpan}
      </span>
    </div>
  );
}

/**
 * Die Miniatur: das echte Widget, klein und bedienungsblind.
 *
 * Gerendert wird erst, wenn die Kachel ins Blickfeld kommt
 * ({@link useInView}). Zweiundzwanzig Widgets auf einmal sind nicht nur
 * zweiundzwanzig Baeume, sondern auch zwei ECharts-Instanzen (Trend,
 * Energiefluss) und ueber hundert Abos, und das alles in dem Moment, in dem
 * der Autor den Editor oeffnet und eigentlich sein Bild sehen will.
 */
function Thumbnail(props: {
  entry: WidgetCatalogEntry;
  preview: WidgetDefinition | null;
  source: IPointBindingSource;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const seen = useInView(hostRef);
  const { entry, preview } = props;
  const geometry = thumbGeometry(entry);

  return (
    <div
      class={'hv-wp__thumb' + (isWide(entry) ? ' hv-wp__thumb--wide' : '')}
      aria-hidden="true"
      /*
       * Bedienungsblind auf zwei Wegen, und beide werden gebraucht.
       *
       * `inert` nimmt die Knoepfe und Regler in der Miniatur aus der
       * Tabulatorreihenfolge und aus dem Bedienungsbaum; ohne das
       * ertastet die Tastatur zweiundzwanzig fremde Widgets, bevor sie
       * beim naechsten Palettenfeld ankommt. Das Stylesheet setzt
       * zusaetzlich `pointer-events: none`, weil `inert` in aelteren
       * Browsern schlicht ignoriert wird und ein Klick auf die Palette
       * sonst etwas an der Anlage schaltet, statt ein Widget aufzunehmen.
       * Dasselbe Mittel benutzen die Rasterflaeche und das KI-Modal.
       *
       * Als Attribut ueber den Verweis gesetzt und nicht als Eigenschaft
       * im JSX: die Typen von Preact kennen `inert` nicht.
       */
      ref={(el) => {
        hostRef.current = el;
        el?.setAttribute('inert', '');
      }}
    >
      {preview === null ? (
        <span class="hv-wp__thumb-none">Braucht ein Kunden-SVG</span>
      ) : seen ? (
        <div
          class="hv-wp__stage"
          style={{
            width: `${geometry.nativeW}px`,
            height: `${geometry.nativeH}px`,
            transform: `scale(${geometry.scale})`,
            left: `${geometry.left}px`,
            top: `${geometry.top}px`,
          }}
        >
          <WidgetHost widget={preview} source={props.source} />
        </div>
      ) : (
        <span class="hv-wp__thumb-wait" />
      )}
    </div>
  );
}

/**
 * War dieses Element schon einmal im Blickfeld?
 *
 * Einmal wahr, immer wahr: wer zurueckscrollt, soll keine Miniatur neu
 * aufbauen sehen, und ein Widget, das beim Vorbeiscrollen laufend ab- und
 * angehaengt wird, kostet mehr als es spart (Trend und Verlaufsstrich holen
 * beim Anhaengen ihre Historie).
 *
 * Ohne `IntersectionObserver` (aeltere Browser, Testumgebungen) gilt alles
 * sofort als sichtbar. Das ist der richtige Ausfall: langsamer, aber
 * vollstaendig.
 */
function useInView(ref: { current: HTMLElement | null }): boolean {
  const [seen, setSeen] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      // Ein Stueck Vorlauf, damit die Miniatur fertig ist, bevor sie oben
      // hereinkommt. Sonst sieht der Autor beim Blaettern jedes Mal kurz
      // einen leeren Rahmen.
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, seen]);

  return seen;
}
